import { isISODate, type ISODate } from "@/engine/date";
import type {
  AttachmentLink,
  Project,
  ProjectRecord,
  RecordImpact,
  RecordKind,
  RecordSectionKey,
  RecordSeverity,
  RecordSource,
  RecordStatus,
  RiskScale,
  TaskActivity,
  TaskComment,
} from "@/engine/types";
import { formatDate } from "@/lib/date-format";
import { newId } from "./tree-ops";

/**
 * Pure operations for project records (issues, risks, decisions, policies, change requests).
 * Links point at schedule tasks, execution tasks and other records; they are pruned when the
 * target disappears, never cascaded.
 */

export const RECORD_KINDS: RecordKind[] = ["issue", "risk", "decision", "policy", "change", "assumption"];

export const RECORD_KIND_LABEL: Record<RecordKind, string> = {
  issue: "이슈",
  risk: "리스크",
  decision: "결정",
  policy: "정책",
  change: "변경 요청",
  assumption: "가정",
};

export const RECORD_KIND_PREFIX: Record<RecordKind, string> = {
  issue: "ISS",
  risk: "RSK",
  decision: "DEC",
  policy: "POL",
  change: "CHG",
  assumption: "ASM",
};

export const RECORD_KIND_HINT: Record<RecordKind, string> = {
  issue: "이미 발생해 대응이 필요한 문제",
  risk: "아직 안 일어났지만 일정·범위에 영향",
  decision: "설계·방식에 대해 합의한 선택과 그 이유",
  policy: "고객사·내부·법규에서 정한 지켜야 할 규칙",
  change: "확정된 범위·요건을 바꾸자는 요청",
  assumption: "아직 확인되지 않았지만 계획의 전제로 둔 사실",
};

/**
 * open: someone has to act. active: in effect (확정된 결정, 시행 중인 정책). closed: 끝났거나 효력 없음.
 */
export type RecordPhase = "open" | "active" | "closed";

export const RECORD_STATUSES: Record<RecordKind, RecordStatus[]> = {
  issue: ["open", "inProgress", "resolved", "closed"],
  risk: ["identified", "mitigating", "occurred", "closed"],
  decision: ["proposed", "accepted", "rejected", "superseded"],
  policy: ["proposed", "accepted", "superseded", "deprecated"],
  change: ["requested", "reviewing", "approved", "rejected", "applied"],
  assumption: ["proposed", "accepted", "rejected", "closed"],
};

const STATUS_LABEL: Record<RecordStatus, string> = {
  open: "열림",
  inProgress: "대응 중",
  resolved: "해결",
  closed: "종결",
  identified: "식별",
  mitigating: "대응 중",
  occurred: "발생",
  proposed: "제안",
  accepted: "확정",
  rejected: "기각",
  superseded: "대체됨",
  deprecated: "폐기",
  requested: "요청",
  reviewing: "검토 중",
  approved: "승인",
  applied: "반영",
};

export function recordStatusLabel(kind: RecordKind, status: RecordStatus): string {
  if (kind === "policy" && status === "accepted") return "시행 중";
  if (kind === "change" && status === "rejected") return "반려";
  if (kind === "assumption" && status === "proposed") return "가정";
  if (kind === "assumption" && status === "accepted") return "유효";
  if (kind === "assumption" && status === "rejected") return "무효";
  return STATUS_LABEL[status];
}

const PHASE: Record<RecordStatus, RecordPhase> = {
  open: "open",
  inProgress: "open",
  resolved: "closed",
  closed: "closed",
  identified: "open",
  mitigating: "open",
  occurred: "open",
  proposed: "open",
  accepted: "active",
  rejected: "closed",
  superseded: "closed",
  deprecated: "closed",
  requested: "open",
  reviewing: "open",
  approved: "open",
  applied: "closed",
};

export function recordPhase(status: RecordStatus): RecordPhase {
  return PHASE[status];
}

export const RECORD_PHASE_LABEL: Record<RecordPhase, string> = {
  open: "처리 필요",
  active: "유효",
  closed: "종결",
};

export const SEVERITY_ORDER: RecordSeverity[] = ["high", "medium", "low"];
export const SEVERITY_LABEL: Record<RecordSeverity, string> = { high: "높음", medium: "보통", low: "낮음" };

export const RISK_SCALES: RiskScale[] = [1, 2, 3, 4, 5];
export const RISK_SCALE_LABEL: Record<RiskScale, string> = { 1: "매우 낮음", 2: "낮음", 3: "보통", 4: "높음", 5: "매우 높음" };

export function riskScore(record: Pick<ProjectRecord, "probability" | "impactScore">): number | null {
  if (!record.probability || !record.impactScore) return null;
  return record.probability * record.impactScore;
}

/** 1–4 낮음, 5–9 보통, 10–16 높음, 17–25 매우 높음. */
export function riskBand(score: number): "low" | "medium" | "high" | "critical" {
  if (score <= 4) return "low";
  if (score <= 9) return "medium";
  if (score <= 16) return "high";
  return "critical";
}

export const RISK_BAND_LABEL = { low: "낮음", medium: "보통", high: "높음", critical: "매우 높음" } as const;

/** 종결이 아닌 리스크만. 행은 가능성 1–5, 열은 영향 1–5. */
export function riskMatrix(records: readonly ProjectRecord[]): number[][] {
  const grid = RISK_SCALES.map(() => RISK_SCALES.map(() => 0));
  for (const record of records) {
    if (record.kind !== "risk" || recordPhase(record.status) === "closed") continue;
    if (!record.probability || !record.impactScore) continue;
    const row = grid[record.probability - 1];
    if (row) row[record.impactScore - 1] = (row[record.impactScore - 1] ?? 0) + 1;
  }
  return grid;
}

function readRiskScale(value: unknown): RiskScale | undefined {
  return typeof value === "number" && Number.isInteger(value) && value >= 1 && value <= 5 ? (value as RiskScale) : undefined;
}

export const SOURCE_ORDER: RecordSource[] = ["client", "internal", "regulation", "vendor"];
export const SOURCE_LABEL: Record<RecordSource, string> = { client: "고객사", internal: "내부", regulation: "법규·규정", vendor: "협력사" };

export const IMPACT_ORDER: RecordImpact[] = ["schedule", "scope", "cost", "quality"];
export const IMPACT_LABEL: Record<RecordImpact, string> = { schedule: "일정", scope: "범위", cost: "원가", quality: "품질" };

/** Kinds where severity and a response deadline make sense. */
export function hasSeverity(kind: RecordKind): boolean {
  return kind === "issue" || kind === "risk" || kind === "change";
}

export function dueLabel(kind: RecordKind): string {
  if (kind === "decision" || kind === "policy") return "시행일";
  if (kind === "change") return "반영 기한";
  if (kind === "assumption") return "확인 기한";
  return "대응 기한";
}

export interface RecordSectionDef {
  key: RecordSectionKey;
  label: string;
  placeholder: string;
}

export const RECORD_SECTIONS: Record<RecordKind, RecordSectionDef[]> = {
  issue: [
    { key: "summary", label: "내용", placeholder: "무엇이 문제인지, 언제 누가 알렸는지" },
    { key: "cause", label: "원인", placeholder: "파악된 원인 (모르면 비워 두세요)" },
    { key: "resolution", label: "조치 및 결과", placeholder: "어떻게 대응했고 결과는 어땠는지" },
  ],
  risk: [
    { key: "summary", label: "내용", placeholder: "어떤 조건에서 일어나고, 일어나면 무엇이 영향을 받는지" },
    { key: "mitigation", label: "대응 계획", placeholder: "예방 조치와 발생 시 대응" },
  ],
  decision: [
    { key: "context", label: "배경", placeholder: "왜 결정이 필요했는지, 제약 조건" },
    { key: "decision", label: "결정", placeholder: "무엇으로 정했는지 한두 문장으로" },
    { key: "alternatives", label: "검토한 대안", placeholder: "고려했지만 택하지 않은 안과 이유" },
    { key: "consequences", label: "영향 및 후속 조치", placeholder: "이 결정으로 달라지는 것, 해야 할 일" },
  ],
  policy: [
    { key: "statement", label: "정책 내용", placeholder: "예: 세션은 30분 무활동 시 만료한다" },
    { key: "applicability", label: "적용 범위", placeholder: "적용되는 시스템·화면·사용자" },
    { key: "exceptions", label: "예외", placeholder: "예외 조건과 승인 절차" },
    { key: "context", label: "근거", placeholder: "근거 문서·조항 (예: 고객사 보안지침 3.2)" },
  ],
  change: [
    { key: "summary", label: "요청 내용", placeholder: "무엇을 어떻게 바꾸자는지" },
    { key: "reason", label: "사유", placeholder: "요청한 이유" },
    { key: "impactAnalysis", label: "영향 분석", placeholder: "일정·공수·비용에 미치는 영향" },
  ],
  assumption: [
    { key: "statement", label: "가정", placeholder: "사실로 두고 가는 전제" },
    { key: "context", label: "근거", placeholder: "왜 그렇게 보는지" },
    { key: "impactAnalysis", label: "틀리면", placeholder: "가정이 깨졌을 때 일정·범위에 미치는 영향" },
  ],
};

export const DEFAULT_STATUS: Record<RecordKind, RecordStatus> = {
  issue: "open",
  risk: "identified",
  decision: "proposed",
  policy: "proposed",
  change: "requested",
  assumption: "proposed",
};

export function recordCode(r: Pick<ProjectRecord, "kind" | "seq">): string {
  return `${RECORD_KIND_PREFIX[r.kind]}-${r.seq}`;
}

export function nextSeq(records: ProjectRecord[], kind: RecordKind): number {
  return records.reduce((m, r) => (r.kind === kind ? Math.max(m, r.seq) : m), 0) + 1;
}

export function makeRecord(partial: Partial<ProjectRecord> & { kind: RecordKind; seq: number }, now = new Date().toISOString()): ProjectRecord {
  return {
    id: newId("r"),
    title: "",
    status: DEFAULT_STATUS[partial.kind],
    assignees: [],
    impacts: [],
    sections: {},
    linkedTaskIds: [],
    linkedExecutionTaskIds: [],
    linkedRecordIds: [],
    attachments: [],
    comments: [],
    activity: [],
    createdAt: now,
    updatedAt: now,
    ...partial,
  };
}

/* ---------------------------------------------------------------- normalization (load/import) */

function strArray(v: unknown): string[] {
  return Array.isArray(v) ? v.filter((x): x is string => typeof x === "string") : [];
}

function pick<T extends string>(v: unknown, allowed: readonly T[]): T | undefined {
  return typeof v === "string" && (allowed as readonly string[]).includes(v) ? (v as T) : undefined;
}

function normalizeAttachment(raw: unknown, now: string): AttachmentLink | null {
  if (!raw || typeof raw !== "object") return null;
  const a = raw as Record<string, unknown>;
  if (typeof a.url !== "string" || !a.url) return null;
  const out: AttachmentLink = {
    id: typeof a.id === "string" && a.id ? a.id : newId("at"),
    title: typeof a.title === "string" && a.title ? a.title : a.url,
    url: a.url,
    addedAt: typeof a.addedAt === "string" ? a.addedAt : now,
  };
  const kind = pick(a.kind, ["link", "file", "image"] as const);
  if (kind) out.kind = kind;
  if (typeof a.size === "number" && Number.isFinite(a.size)) out.size = a.size;
  if (typeof a.mimeType === "string" && a.mimeType) out.mimeType = a.mimeType;
  if (typeof a.addedBy === "string" && a.addedBy.trim()) out.addedBy = a.addedBy.trim();
  return out;
}

function normalizeAttachments(raw: unknown, now: string): AttachmentLink[] {
  return Array.isArray(raw) ? raw.map((a) => normalizeAttachment(a, now)).filter((a): a is AttachmentLink => !!a) : [];
}

function normalizeComment(raw: unknown, now: string): TaskComment | null {
  if (!raw || typeof raw !== "object") return null;
  const c = raw as Record<string, unknown>;
  if (typeof c.body !== "string") return null;
  const out: TaskComment = {
    id: typeof c.id === "string" && c.id ? c.id : newId("cm"),
    author: typeof c.author === "string" ? c.author : "",
    body: c.body,
    at: typeof c.at === "string" ? c.at : now,
  };
  if (typeof c.parentId === "string" && c.parentId) out.parentId = c.parentId;
  if (typeof c.updatedAt === "string") out.updatedAt = c.updatedAt;
  if (c.deleted === true) {
    out.deleted = true;
    if (typeof c.deletedAt === "string") out.deletedAt = c.deletedAt;
  }
  const attachments = normalizeAttachments(c.attachments, now);
  if (attachments.length) out.attachments = attachments;
  return out;
}

function normalizeRecord(raw: unknown, now: string): ProjectRecord | null {
  if (!raw || typeof raw !== "object") return null;
  const r = raw as Record<string, unknown>;
  const kind = pick(r.kind, RECORD_KINDS);
  if (typeof r.id !== "string" || !r.id || !kind) return null;
  const seq = typeof r.seq === "number" && Number.isFinite(r.seq) && r.seq > 0 ? Math.round(r.seq) : 0;
  const rec = makeRecord({ id: r.id, kind, seq }, now);
  rec.title = typeof r.title === "string" ? r.title : "";
  if (typeof r.author === "string" && r.author.trim()) rec.author = r.author.trim();
  rec.status = pick(r.status, RECORD_STATUSES[kind]) ?? DEFAULT_STATUS[kind];
  const severity = pick(r.severity, SEVERITY_ORDER);
  if (severity) rec.severity = severity;
  if (kind === "risk") {
    const probability = readRiskScale(r.probability);
    const impactScore = readRiskScale(r.impactScore);
    if (probability) rec.probability = probability;
    if (impactScore) rec.impactScore = impactScore;
  }
  const source = pick(r.source, SOURCE_ORDER);
  if (source) rec.source = source;
  const assignees = Array.isArray(r.assignees) ? strArray(r.assignees) : typeof r.owner === "string" ? [r.owner] : [];
  rec.assignees = [...new Set(assignees.map((n) => n.trim()).filter(Boolean))];
  if (isISODate(r.due)) rec.due = r.due;
  rec.impacts = strArray(r.impacts).filter((x): x is RecordImpact => (IMPACT_ORDER as string[]).includes(x));
  if (r.sections && typeof r.sections === "object") {
    for (const [k, v] of Object.entries(r.sections as Record<string, unknown>)) {
      if (typeof v === "string" && v) rec.sections[k as RecordSectionKey] = v;
    }
  }
  if (typeof r.supersedes === "string" && r.supersedes) rec.supersedes = r.supersedes;
  rec.linkedTaskIds = strArray(r.linkedTaskIds);
  rec.linkedExecutionTaskIds = strArray(r.linkedExecutionTaskIds);
  rec.linkedRecordIds = strArray(r.linkedRecordIds);
  rec.attachments = normalizeAttachments(Array.isArray(r.attachments) ? r.attachments : r.links, now);
  rec.comments = Array.isArray(r.comments) ? r.comments.map((c) => normalizeComment(c, now)).filter((c): c is TaskComment => !!c) : [];
  rec.activity = Array.isArray(r.activity)
    ? r.activity
        .filter((a): a is TaskActivity => !!a && typeof a === "object" && typeof (a as { message?: unknown }).message === "string")
        .map((a) => ({ id: typeof a.id === "string" ? a.id : newId("ac"), at: typeof a.at === "string" ? a.at : now, actor: typeof a.actor === "string" ? a.actor : "", message: a.message }))
    : [];
  if (typeof r.closedAt === "string") rec.closedAt = r.closedAt;
  rec.createdAt = typeof r.createdAt === "string" ? r.createdAt : now;
  rec.updatedAt = typeof r.updatedAt === "string" ? r.updatedAt : now;
  return rec;
}

/**
 * Records for a project that may predate them. Drops duplicates and dangling links, and gives
 * records without a valid number the next free one of their kind. Idempotent.
 */
export function normalizeRecords(project: Pick<Project, "tasks"> & Partial<Pick<Project, "executionTasks" | "records">>): ProjectRecord[] {
  const now = new Date().toISOString();
  const seen = new Set<string>();
  const list: ProjectRecord[] = [];
  for (const x of Array.isArray(project.records) ? project.records : []) {
    const r = normalizeRecord(x, now);
    if (!r || seen.has(r.id)) continue;
    seen.add(r.id);
    list.push(r);
  }
  const used = new Map<RecordKind, Set<number>>();
  for (const r of list) {
    const set = used.get(r.kind) ?? new Set<number>();
    if (r.seq && !set.has(r.seq)) set.add(r.seq);
    else r.seq = 0;
    used.set(r.kind, set);
  }
  for (const r of list) {
    if (r.seq) continue;
    const set = used.get(r.kind)!;
    let n = 1;
    while (set.has(n)) n++;
    set.add(n);
    r.seq = n;
  }
  return pruneRecordLinks(list, {
    taskIds: project.tasks.map((t) => t.id),
    executionTaskIds: (project.executionTasks ?? []).map((t) => t.id),
  });
}

/* ---------------------------------------------------------------- links */

/** Drop links whose target no longer exists. Pass only the id sets that may have shrunk. */
export function pruneRecordLinks(
  list: ProjectRecord[],
  known: { taskIds?: Iterable<string>; executionTaskIds?: Iterable<string> },
): ProjectRecord[] {
  const tasks = known.taskIds ? new Set(known.taskIds) : null;
  const xs = known.executionTaskIds ? new Set(known.executionTaskIds) : null;
  const ids = new Set(list.map((r) => r.id));
  let changed = false;
  const out = list.map((r) => {
    const linkedTaskIds = tasks ? r.linkedTaskIds.filter((id) => tasks.has(id)) : r.linkedTaskIds;
    const linkedExecutionTaskIds = xs ? r.linkedExecutionTaskIds.filter((id) => xs.has(id)) : r.linkedExecutionTaskIds;
    const linkedRecordIds = r.linkedRecordIds.filter((id) => ids.has(id) && id !== r.id);
    const supersedes = r.supersedes && ids.has(r.supersedes) && r.supersedes !== r.id ? r.supersedes : undefined;
    if (
      linkedTaskIds.length === r.linkedTaskIds.length &&
      linkedExecutionTaskIds.length === r.linkedExecutionTaskIds.length &&
      linkedRecordIds.length === r.linkedRecordIds.length &&
      supersedes === r.supersedes
    ) {
      return r;
    }
    changed = true;
    const next: ProjectRecord = { ...r, linkedTaskIds, linkedExecutionTaskIds, linkedRecordIds };
    if (supersedes) next.supersedes = supersedes;
    else delete next.supersedes;
    return next;
  });
  return changed ? out : list;
}

export function removeRecords(list: ProjectRecord[], ids: string[]): ProjectRecord[] {
  const gone = new Set(ids);
  return pruneRecordLinks(
    list.filter((r) => !gone.has(r.id)),
    {},
  );
}

/** Records pointing at `id` in any way (related link, supersedes), for "이 기록을 참조하는 기록". */
export function recordsReferencing(list: ProjectRecord[], id: string): ProjectRecord[] {
  return list.filter((r) => r.id !== id && (r.linkedRecordIds.includes(id) || r.supersedes === id));
}

export function recordsForExecutionTask(list: ProjectRecord[], executionTaskId: string): ProjectRecord[] {
  return list.filter((r) => r.linkedExecutionTaskIds.includes(executionTaskId));
}

export function recordsForScheduleTask(list: ProjectRecord[], scheduleTaskId: string): ProjectRecord[] {
  return list.filter((r) => r.linkedTaskIds.includes(scheduleTaskId));
}

/** The record that replaced `id`, if any (newest first when several claim it). */
export function supersededBy(list: ProjectRecord[], id: string): ProjectRecord | undefined {
  return list.filter((r) => r.supersedes === id).sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1))[0];
}

/* ---------------------------------------------------------------- lifecycle */

export function logRecordActivity(r: ProjectRecord, actor: string, message: string, now = new Date().toISOString()): ProjectRecord {
  return { ...r, activity: [{ id: newId("ac"), at: now, actor, message }, ...r.activity].slice(0, 200) };
}

/** Keep `closedAt` in step with the status phase. */
export function withClosedAt(r: ProjectRecord, now: string): ProjectRecord {
  const closed = recordPhase(r.status) === "closed";
  if (closed && !r.closedAt) return { ...r, closedAt: now };
  if (!closed && r.closedAt) {
    const { closedAt: _drop, ...rest } = r;
    return rest;
  }
  return r;
}

/**
 * Once a record that supersedes another is in effect, the older one stops being in effect.
 * Only touches a predecessor that is still `accepted`, so a rejected or already replaced one stays as it was.
 */
export function applySupersede(list: ProjectRecord[], id: string, actor: string, now = new Date().toISOString()): ProjectRecord[] {
  const cur = list.find((r) => r.id === id);
  if (!cur?.supersedes || cur.status !== "accepted") return list;
  const prev = list.find((r) => r.id === cur.supersedes);
  if (!prev || prev.status !== "accepted") return list;
  return list.map((r) =>
    r.id === prev.id ? logRecordActivity(withClosedAt({ ...r, status: "superseded", updatedAt: now }, now), actor, `${recordCode(cur)}로 대체됨`, now) : r,
  );
}

/** Draft a revision of a decision/policy: same text and links, new number, starts as 제안. */
export function makeRevision(list: ProjectRecord[], id: string, now = new Date().toISOString()): ProjectRecord | null {
  const src = list.find((r) => r.id === id);
  if (!src) return null;
  return makeRecord(
    {
      kind: src.kind,
      seq: nextSeq(list, src.kind),
      title: src.title,
      source: src.source,
      assignees: [...src.assignees],
      impacts: [...src.impacts],
      sections: { ...src.sections },
      supersedes: src.id,
      linkedTaskIds: [...src.linkedTaskIds],
      linkedExecutionTaskIds: [...src.linkedExecutionTaskIds],
      linkedRecordIds: [...src.linkedRecordIds],
      attachments: src.attachments.map((a) => ({ ...a, id: newId("at") })),
    },
    now,
  );
}

/** A risk that happened becomes an issue; the two stay linked. */
export function makeIssueFromRisk(list: ProjectRecord[], riskId: string, now = new Date().toISOString()): ProjectRecord | null {
  const risk = list.find((r) => r.id === riskId && r.kind === "risk");
  if (!risk) return null;
  return makeRecord(
    {
      kind: "issue",
      seq: nextSeq(list, "issue"),
      title: risk.title,
      severity: risk.severity,
      source: risk.source,
      assignees: [...risk.assignees],
      impacts: [...risk.impacts],
      sections: risk.sections.summary ? { summary: risk.sections.summary } : {},
      linkedTaskIds: [...risk.linkedTaskIds],
      linkedExecutionTaskIds: [...risk.linkedExecutionTaskIds],
      linkedRecordIds: [risk.id],
    },
    now,
  );
}

/**
 * Append `incoming` after `existing` without touching it: ids that collide get a fresh id, numbers
 * continue after each kind's current max, and links between incoming records follow the remap.
 */
export function appendRecords(existing: ProjectRecord[], incoming: ProjectRecord[]): ProjectRecord[] {
  const taken = new Set(existing.map((r) => r.id));
  const idMap = new Map<string, string>();
  for (const r of incoming) idMap.set(r.id, taken.has(r.id) ? newId("r") : r.id);
  const offset = new Map<RecordKind, number>(RECORD_KINDS.map((k) => [k, nextSeq(existing, k) - 1]));
  const remap = (id: string) => idMap.get(id) ?? id;
  const added = incoming.map((r) => {
    const next: ProjectRecord = {
      ...structuredClone(r),
      id: remap(r.id),
      seq: r.seq + (offset.get(r.kind) ?? 0),
      linkedRecordIds: r.linkedRecordIds.map(remap),
    };
    if (r.supersedes) next.supersedes = remap(r.supersedes);
    return next;
  });
  return [...existing, ...added];
}

/* ---------------------------------------------------------------- derived facts */

export function isRecordOverdue(r: ProjectRecord, today: ISODate): boolean {
  return !!r.due && hasSeverity(r.kind) && recordPhase(r.status) === "open" && r.due < today;
}

export interface RecordSummary {
  openIssues: number;
  highOpenIssues: number;
  openRisks: number;
  pendingDecisions: number;
  overdue: number;
}

export function summarizeRecords(list: ProjectRecord[], today: ISODate): RecordSummary {
  const s: RecordSummary = { openIssues: 0, highOpenIssues: 0, openRisks: 0, pendingDecisions: 0, overdue: 0 };
  for (const r of list) {
    const open = recordPhase(r.status) === "open";
    if (!open) continue;
    if (r.kind === "issue") {
      s.openIssues++;
      if (r.severity === "high") s.highOpenIssues++;
    } else if (r.kind === "risk") s.openRisks++;
    else if (r.kind === "decision" || r.kind === "policy" || r.kind === "change") s.pendingDecisions++;
    if (isRecordOverdue(r, today)) s.overdue++;
  }
  return s;
}

/** Human-readable activity line for a field patch (null → nothing worth logging). */
export function describeRecordPatch(cur: ProjectRecord, patch: Partial<ProjectRecord>, list: ProjectRecord[]): string | null {
  const parts: string[] = [];
  if ("title" in patch && patch.title !== cur.title) parts.push(`제목 → ${patch.title || "(없음)"}`);
  if ("status" in patch && patch.status && patch.status !== cur.status) parts.push(`상태 → ${recordStatusLabel(cur.kind, patch.status)}`);
  if ("severity" in patch && patch.severity !== cur.severity) parts.push(`심각도 → ${patch.severity ? SEVERITY_LABEL[patch.severity] : "미정"}`);
  if ("probability" in patch && patch.probability !== cur.probability) parts.push(`가능성 → ${patch.probability ? RISK_SCALE_LABEL[patch.probability] : "미정"}`);
  if ("impactScore" in patch && patch.impactScore !== cur.impactScore) parts.push(`영향 정도 → ${patch.impactScore ? RISK_SCALE_LABEL[patch.impactScore] : "미정"}`);
  if ("source" in patch && patch.source !== cur.source) parts.push(`출처 → ${patch.source ? SOURCE_LABEL[patch.source] : "미정"}`);
  if ("assignees" in patch && patch.assignees && patch.assignees.join("|") !== cur.assignees.join("|")) parts.push(`담당 → ${patch.assignees.join(", ") || "미정"}`);
  if ("due" in patch && patch.due !== cur.due) parts.push(`${dueLabel(cur.kind)} → ${formatDate(patch.due, "numeric", { empty: "미정" })}`);
  if ("impacts" in patch && patch.impacts && patch.impacts.join("|") !== cur.impacts.join("|")) {
    parts.push(`영향 → ${patch.impacts.length ? patch.impacts.map((i) => IMPACT_LABEL[i]).join(", ") : "없음"}`);
  }
  if ("sections" in patch && patch.sections) {
    const changed = RECORD_SECTIONS[cur.kind].filter((d) => (patch.sections![d.key] ?? "") !== (cur.sections[d.key] ?? "")).map((d) => d.label);
    if (changed.length) parts.push(`${changed.join(", ")} 수정`);
  }
  if ("supersedes" in patch && patch.supersedes !== cur.supersedes) {
    const target = list.find((r) => r.id === patch.supersedes);
    parts.push(target ? `${recordCode(target)} 대체` : "대체 관계 해제");
  }
  if ("linkedTaskIds" in patch && patch.linkedTaskIds && patch.linkedTaskIds.join("|") !== cur.linkedTaskIds.join("|")) parts.push(`연결 WBS ${patch.linkedTaskIds.length}개`);
  if ("linkedExecutionTaskIds" in patch && patch.linkedExecutionTaskIds && patch.linkedExecutionTaskIds.join("|") !== cur.linkedExecutionTaskIds.join("|")) {
    parts.push(`연결 Task ${patch.linkedExecutionTaskIds.length}개`);
  }
  if ("linkedRecordIds" in patch && patch.linkedRecordIds && patch.linkedRecordIds.join("|") !== cur.linkedRecordIds.join("|")) parts.push(`관련 기록 ${patch.linkedRecordIds.length}개`);
  if ("attachments" in patch && patch.attachments) {
    const before = new Set(cur.attachments.map((a) => a.id));
    const after = new Set(patch.attachments.map((a) => a.id));
    const added = patch.attachments.filter((a) => !before.has(a.id));
    const removed = cur.attachments.filter((a) => !after.has(a.id));
    if (added.length) parts.push(`첨부 추가: ${added.map((a) => a.title).join(", ")}`);
    if (removed.length) parts.push(`첨부 삭제: ${removed.map((a) => a.title).join(", ")}`);
  }
  return parts.length ? parts.join(" · ") : null;
}
